import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Plus } from "lucide-react";
import { departmentService } from "#/features/departments/api";
import { ErrorState } from "#/components/common/ErrorState";
import { EmptyState } from "#/components/common/EmptyState";
import { PageLoader } from "#/components/common/PageLoader";
import { useToast } from "#/components/common/ToastProvider";
import { useConfirm } from "#/components/common/ConfirmProvider";
import type { DepartmentResponse } from "#/features/departments/types";
import type { PageResponse } from "#/types/api";

type UserOption = { id: number; fullName: string; username: string };
type Tab = "active" | "inactive";

// Departments are a small, admin-configured list (departmentOptions() elsewhere in this
// feature already assumes the whole set fits in one page of 100), so this page fetches
// everything once and splits it into Active / Inactive tabs client-side rather than paging.
const FETCH_SIZE = 200;

export default function DepartmentPage() {
  const toast = useToast();
  const confirm = useConfirm();

  const [data, setData] = useState<PageResponse<DepartmentResponse> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("active");

  // Which department row currently has an action (delete/deactivate/reactivate) in flight —
  // disables just that row's buttons rather than the whole page.
  const [busyId, setBusyId] = useState<number | null>(null);

  // Users loaded on mount so card names resolve without opening the modal
  const [users, setUsers] = useState<UserOption[]>([]);
  // Lookup map: userId → display name
  const userNameMap = new Map(users.map((u) => [u.id, u.fullName || u.username]));

  // Head-of-department assignment
  const [assigningDept, setAssigningDept] = useState<DepartmentResponse | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | "">("");
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const result = await departmentService.list({ page: 0, size: FETCH_SIZE });
      setData(result);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to load departments");
    } finally {
      setLoading(false);
    }
  }

  // Load departments AND users in parallel on mount so head names display immediately
  useEffect(() => {
    void load();
    departmentService.userOptions()
      .then(setUsers)
      .catch(() => { /* non-critical — names will fall back to IDs */ });
  }, []);

  async function openAssign(dept: DepartmentResponse) {
    setAssigningDept(dept);
    setSelectedUserId(dept.headUserId ?? "");
    setAssignError(null);
  }

  async function submitAssign() {
    if (!assigningDept) return;
    setAssigning(true);
    setAssignError(null);
    try {
      await departmentService.assignHead(
        assigningDept.id,
        selectedUserId !== "" ? Number(selectedUserId) : null,
      );
      setAssigningDept(null);
      await load();
    } catch (err: any) {
      setAssignError(err?.response?.data?.message ?? "Failed to assign head of department.");
    } finally {
      setAssigning(false);
    }
  }

  async function handleDeactivate(dept: DepartmentResponse) {
    if (!(await confirm(
      `Deactivate "${dept.name}"? It will move to the Inactive tab, stay editable, and can be reactivated at any time.`,
      { title: "Deactivate department", confirmText: "Deactivate" },
    ))) return;

    setBusyId(dept.id);
    try {
      await departmentService.deactivate(dept.id);
      toast.success(`"${dept.name}" deactivated.`);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to deactivate department.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleReactivate(dept: DepartmentResponse) {
    setBusyId(dept.id);
    try {
      await departmentService.reactivate(dept.id);
      toast.success(`"${dept.name}" reactivated.`);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to reactivate department.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(dept: DepartmentResponse) {
    if (!(await confirm(
      `Permanently delete "${dept.name}"? This can't be undone.`,
      { title: "Delete department", confirmText: "Delete" },
    ))) return;

    setBusyId(dept.id);
    try {
      await departmentService.remove(dept.id);
      toast.success(`"${dept.name}" permanently deleted.`);
      await load();
    } catch (err: any) {
      // Backend refuses (400) when employees, users, or records in another module still
      // reference this department. Offer the always-safe alternative instead of a dead end.
      const message: string = err?.response?.data?.message ?? "Failed to delete department.";
      setBusyId(null);
      if (await confirm(
        `${message} Deactivate it instead?`,
        { title: "Can't permanently delete", confirmText: "Deactivate instead" },
      )) {
        await handleDeactivate(dept);
      }
      return;
    }
    setBusyId(null);
  }

  if (loading) return <PageLoader />;
  if (error) return <ErrorState message={error} />;

  if (!data || data.content.length === 0) {
    return (
      <EmptyState
        title="No departments found"
        description="Departments configured on the backend will appear here."
      />
    );
  }

  const activeDepts = data.content.filter((d) => d.active);
  const inactiveDepts = data.content.filter((d) => !d.active);
  const shown = tab === "active" ? activeDepts : inactiveDepts;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
            <Building2 className="h-5 w-5" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Departments</h1>
        </div>
        <Link
          to="/admin/departments/new"
          className="flex items-center gap-1.5 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
        >
          <Plus className="h-4 w-4" />
          New Department
        </Link>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        <button
          type="button"
          onClick={() => setTab("active")}
          className={`px-4 py-2 text-sm font-semibold ${
            tab === "active"
              ? "border-b-2 border-emerald-600 text-emerald-700"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          Active ({activeDepts.length})
        </button>
        <button
          type="button"
          onClick={() => setTab("inactive")}
          className={`px-4 py-2 text-sm font-semibold ${
            tab === "inactive"
              ? "border-b-2 border-emerald-600 text-emerald-700"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          Inactive ({inactiveDepts.length})
        </button>
      </div>

      {shown.length === 0 ? (
        <EmptyState
          title={tab === "active" ? "No active departments" : "No inactive departments"}
          description={
            tab === "active"
              ? "Every department is currently deactivated."
              : "Deactivated departments will appear here — they stay editable and can be reactivated at any time."
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((dept) => {
            const rowBusy = busyId === dept.id;
            return (
              <div key={dept.id} className="flex flex-col rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">{dept.code}</p>
                <h3 className="mt-1 text-lg font-semibold text-slate-900">{dept.name}</h3>
                {dept.description && <p className="mt-1 text-sm text-slate-500">{dept.description}</p>}

                {/* Head of Department */}
                {(() => {
                  // Prefer the server-resolved employee name, then the separately-fetched
                  // user list's name, then fall back to the bare login username.
                  const resolvedName =
                    dept.headEmployeeName ??
                    (dept.headUserId ? userNameMap.get(dept.headUserId) : null) ??
                    dept.headUsername ??
                    null;
                  const assigned = !!dept.headUserId;
                  const initial = resolvedName ? resolvedName.charAt(0).toUpperCase() : "!";
                  return (
                    <div className="mt-4 flex items-center gap-3 rounded-xl bg-slate-50 px-3 py-2.5">
                      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${assigned ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-600"}`}>
                        {initial}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Head of Department</p>
                        {assigned ? (
                          <p className="truncate text-sm font-semibold text-slate-800">
                            {resolvedName ?? `User #${dept.headUserId}`}
                          </p>
                        ) : (
                          <p className="text-sm font-medium text-amber-600">Not assigned — workflows may fail</p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <div className="mt-4 flex items-center justify-between gap-2">
                  <span className={`text-xs font-semibold ${dept.active ? "text-emerald-600" : "text-slate-400"}`}>
                    {dept.active ? "Active" : "Inactive"}
                  </span>
                  <div className="flex flex-wrap justify-end gap-2">
                    <Link
                      to={`/admin/departments/${dept.id}/edit`}
                      className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Edit
                    </Link>
                    {dept.active && (
                      <button
                        type="button"
                        onClick={() => openAssign(dept)}
                        className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                      >
                        {dept.headUserId ? "Change Head" : "Assign Head"}
                      </button>
                    )}
                    {dept.active ? (
                      <button
                        type="button"
                        disabled={rowBusy}
                        onClick={() => handleDeactivate(dept)}
                        className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={rowBusy}
                        onClick={() => handleReactivate(dept)}
                        className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                      >
                        Reactivate
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={rowBusy}
                      onClick={() => handleDelete(dept)}
                      className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Assign Head modal */}
      {assigningDept && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-bold text-slate-900">Head of {assigningDept.name}</h2>

            {assigningDept.headUserId && (
              <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-sm">
                Current head:{" "}
                <strong>
                  {assigningDept.headEmployeeName ??
                    userNameMap.get(assigningDept.headUserId) ??
                    assigningDept.headUsername ??
                    `User #${assigningDept.headUserId}`}
                </strong>
              </div>
            )}

            <div className="mt-4">
              <label className="block space-y-1.5 text-sm">
                <span className="font-medium text-slate-700">Select user as head of department</span>
                <select
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value !== "" ? Number(e.target.value) : "")}
                >
                  <option value="">— Remove head (leave unassigned) —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.fullName}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {assignError && (
              <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{assignError}</p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setAssigningDept(null)}
                className="rounded-xl border px-4 py-2 text-sm font-semibold">
                Cancel
              </button>
              <button type="button" disabled={assigning} onClick={submitAssign}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {assigning ? "Saving…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
